import React, { useState, useEffect } from 'react';
import supplierPaymentService from '../../../services/supplierPaymentService';
import { Link } from 'react-router-dom';
import './SupplierPaymentReports.css';
import { FiEye, FiDownload, FiFilter, FiDollarSign, FiUsers, FiFileText, FiCalendar } from 'react-icons/fi';

const SupplierPendingPaymentsReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'total_pending_amount', direction: 'desc' });

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const data = await supplierPaymentService.getPendingPaymentsReport();
      setReportData(data);
    } catch (err) {
      setError('Failed to fetch report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedSuppliers = () => {
    if (!reportData?.suppliers) return [];
    
    const sorted = [...reportData.suppliers];
    sorted.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    // Apply search filter
    return sorted.filter(supplier =>
      supplier.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.supplier_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.contact_person.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleExportCSV = () => {
    if (!reportData?.suppliers) return;
    
    const headers = ['Supplier Code', 'Supplier Name', 'Contact Person', 'Email', 'Phone', 'Pending Amount', 'Pending Orders'];
    const csvContent = [
      headers.join(','),
      ...reportData.suppliers.map(supplier => [
        `"${supplier.supplier_code}"`,
        `"${supplier.supplier_name}"`,
        `"${supplier.contact_person}"`,
        `"${supplier.email}"`,
        `"${supplier.phone}"`,
        supplier.total_pending_amount,
        supplier.pending_orders_count
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pending-payments-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="sp-report-container">
        <div className="sp-report-skeleton">
          <div className="sp-skeleton-header"></div>
          <div className="sp-skeleton-card"></div>
          <div className="sp-skeleton-table"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sp-report-container">
        <div className="sp-report-error">
          <h3>{error}</h3>
          <button onClick={fetchReportData} className="sp-retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-report-container">
      <div className="sp-report-header">
        <h1 className="sp-report-title">
          <FiDollarSign className="sp-header-icon" />
          Supplier Pending Payments Report
        </h1>
        <div className="sp-report-actions">
          <button onClick={handleExportCSV} className="sp-export-button">
            <FiDownload /> Export CSV
          </button>
          <button onClick={fetchReportData} className="sp-refresh-button">
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="sp-summary-cards">
        <div className="sp-summary-card sp-card-primary">
          <div className="sp-card-icon">
            <FiUsers />
          </div>
          <div className="sp-card-content">
            <h3 className="sp-card-title">Suppliers with Pending</h3>
            <p className="sp-card-value">{reportData?.overall_summary?.total_suppliers_with_pending || 0}</p>
          </div>
        </div>
        
        <div className="sp-summary-card sp-card-warning">
          <div className="sp-card-icon">
            <FiDollarSign />
          </div>
          <div className="sp-card-content">
            <h3 className="sp-card-title">Total Pending Amount</h3>
            <p className="sp-card-value">
              {formatCurrency(reportData?.overall_summary?.total_pending_amount || 0)}
            </p>
          </div>
        </div>
        
        <div className="sp-summary-card sp-card-info">
          <div className="sp-card-icon">
            <FiFileText />
          </div>
          <div className="sp-card-content">
            <h3 className="sp-card-title">Pending Orders</h3>
            <p className="sp-card-value">{reportData?.overall_summary?.total_pending_orders || 0}</p>
          </div>
        </div>
        
        <div className="sp-summary-card sp-card-secondary">
          <div className="sp-card-icon">
            <FiCalendar />
          </div>
          <div className="sp-card-content">
            <h3 className="sp-card-title">Report Date</h3>
            <p className="sp-card-value">
              {new Date(reportData?.overall_summary?.report_generated_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="sp-report-controls">
        <div className="sp-search-box">
          <FiFilter />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sp-search-input"
          />
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="sp-suppliers-table">
        <div className="sp-table-wrapper">
          <table className="sp-data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('supplier_code')}>
                  Supplier Code {sortConfig.key === 'supplier_code' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('supplier_name')}>
                  Supplier Name {sortConfig.key === 'supplier_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Contact Person</th>
                <th>Contact Info</th>
                <th onClick={() => handleSort('pending_orders_count')}>
                  Pending Orders {sortConfig.key === 'pending_orders_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('total_pending_amount')}>
                  Pending Amount {sortConfig.key === 'total_pending_amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSuppliers().map((supplier) => (
                <tr key={supplier.supplier_id} className="sp-supplier-row">
                  <td className="sp-supplier-code">
                    <span className="sp-code-badge">{supplier.supplier_code}</span>
                  </td>
                  <td className="sp-supplier-name">
                    <strong>{supplier.supplier_name}</strong>
                  </td>
                  <td>{supplier.contact_person || 'N/A'}</td>
                  <td>
                    <div className="sp-contact-info">
                      <div>{supplier.email}</div>
                      <div className="sp-phone-text">{supplier.phone}</div>
                    </div>
                  </td>
                  <td>
                    <span className="sp-order-count">
                      {supplier.pending_orders_count} orders
                    </span>
                  </td>
                  <td>
                    <div className="sp-pending-amount">
                      <span className={`sp-amount-badge ${supplier.total_pending_amount > 100000 ? 'sp-high-pending' : 'sp-normal-pending'}`}>
                        {formatCurrency(supplier.total_pending_amount)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="sp-action-buttons">
                      <Link
                        to={`/suppliers/${supplier.supplier_id}/pending-details`}
                        className="sp-view-button"
                      >
                        <FiEye /> Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedSuppliers().length === 0 && (
                <tr>
                  <td colSpan="7" className="sp-no-data">
                    {searchTerm ? 'No suppliers found matching your search' : 'No pending payments found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Report Summary */}
      {reportData?.overall_summary && (
        <div className="sp-report-summary">
          <h3>Report Summary</h3>
          <div className="sp-summary-grid">
            <div className="sp-summary-item">
              <span className="sp-summary-label">Report Generated:</span>
              <span className="sp-summary-value">
                {new Date(reportData.overall_summary.report_generated_at).toLocaleString()}
              </span>
            </div>
            <div className="sp-summary-item">
              <span className="sp-summary-label">Total Suppliers:</span>
              <span className="sp-summary-value">
                {reportData.overall_summary.total_suppliers_with_pending}
              </span>
            </div>
            <div className="sp-summary-item">
              <span className="sp-summary-label">Total Pending Amount:</span>
              <span className="sp-summary-value sp-highlight-value">
                {formatCurrency(reportData.overall_summary.total_pending_amount)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierPendingPaymentsReport;